import { apiClient } from './api-client';
import type {
  Pipeline,
  CreatePipelineDto,
  UpdatePipelineDto,
  Stage,
  CreateStageDto,
  UpdateStageDto,
  PipelineConfig,
  PipelineFieldSchema,
  PipelineAIConfig,
  PipelineSLAConfig,
  PipelineRoutingConfig,
  PipelineWorkflowConfig,
  PipelineEmailConfig,
} from '@/types/pipeline';

export const pipelineService = {
  // Pipeline CRUD
  async getPipelines(): Promise<Pipeline[]> {
    const response = await apiClient.get('/tickets/pipelines');
    return response.data;
  },

  async getPipelineById(id: string): Promise<Pipeline> {
    const response = await apiClient.get(`/tickets/pipelines/${id}`);
    return response.data;
  },

  async getDefaultPipeline(): Promise<Pipeline> {
    const response = await apiClient.get('/tickets/pipelines/default');
    return response.data;
  },

  async createPipeline(data: CreatePipelineDto): Promise<Pipeline> {
    const response = await apiClient.post('/tickets/pipelines', data);
    return response.data;
  },

  async updatePipeline(id: string, data: UpdatePipelineDto): Promise<Pipeline> {
    const response = await apiClient.patch(`/tickets/pipelines/${id}`, data);
    return response.data;
  },

  async deletePipeline(id: string): Promise<void> {
    await apiClient.delete(`/tickets/pipelines/${id}`);
  },

  // Stage CRUD
  async getStages(pipelineId: string): Promise<Stage[]> {
    const response = await apiClient.get(`/tickets/pipelines/${pipelineId}/stages`);
    return response.data;
  },

  async createStage(pipelineId: string, data: CreateStageDto): Promise<Stage> {
    const response = await apiClient.post(`/tickets/pipelines/${pipelineId}/stages`, data);
    return response.data;
  },

  async updateStage(stageId: string, data: UpdateStageDto): Promise<Stage> {
    const response = await apiClient.patch(`/tickets/pipelines/stages/${stageId}`, data);
    return response.data;
  },

  async deleteStage(stageId: string): Promise<void> {
    await apiClient.delete(`/tickets/pipelines/stages/${stageId}`);
  },

  async reorderStages(pipelineId: string, stageIds: string[]): Promise<void> {
    await apiClient.patch(`/tickets/pipelines/${pipelineId}/stages/reorder`, { stageIds });
  },

  // Pipeline Configuration
  async getPipelineConfig(pipelineId: string): Promise<PipelineConfig> {
    const response = await apiClient.get(`/tickets/pipelines/${pipelineId}/config`);
    return response.data;
  },

  async updateFieldSchema(pipelineId: string, fieldSchema: PipelineFieldSchema): Promise<Pipeline> {
    const response = await apiClient.patch(`/tickets/pipelines/${pipelineId}/config/field-schema`, { fieldSchema });
    return response.data;
  },

  async updateAIConfig(pipelineId: string, aiConfig: PipelineAIConfig): Promise<Pipeline> {
    const response = await apiClient.patch(`/tickets/pipelines/${pipelineId}/config/ai`, { aiConfig });
    return response.data;
  },

  async updateSLAConfig(pipelineId: string, slaConfig: PipelineSLAConfig): Promise<Pipeline> {
    const response = await apiClient.patch(`/tickets/pipelines/${pipelineId}/config/sla`, { slaConfig });
    return response.data;
  },

  async updateRoutingConfig(pipelineId: string, routingConfig: PipelineRoutingConfig): Promise<Pipeline> {
    const response = await apiClient.patch(`/tickets/pipelines/${pipelineId}/config/routing`, { routingConfig });
    return response.data;
  },

  async updateWorkflowConfig(pipelineId: string, workflowConfig: PipelineWorkflowConfig): Promise<Pipeline> {
    const response = await apiClient.patch(`/tickets/pipelines/${pipelineId}/config/workflow`, { workflowConfig });
    return response.data;
  },

  async updateEmailConfig(pipelineId: string, emailConfig: PipelineEmailConfig): Promise<Pipeline> {
    const response = await apiClient.patch(`/tickets/pipelines/${pipelineId}/config/email`, { emailConfig });
    return response.data;
  },

  // Form schema for dynamic UI rendering
  async getFormSchema(pipelineId: string): Promise<PipelineFieldSchema> {
    const response = await apiClient.get(`/tickets/pipelines/${pipelineId}/form-schema`);
    return response.data;
  },
};

export default pipelineService;
